import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { RestApiError } from '@/api/RestApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import styles from './StartCampaign.module.css';

/**
 * The page a text links to when a campaign is ready to start.
 *
 * The SMS thread used to ask the owner to type a four-character code back. A
 * text cannot show them the messages, the recipient count or the schedule, so
 * that code agreed to real customer sends sight unseen. This page shows what
 * will go out first, and the app's own IMS sign-in ties the action to the owner
 * rather than to whoever sent a text.
 */

type CampaignProposal = {
	id: string;
	title?: string;
	aim?: string;
	status?: string;
	process_id?: string;
	steps?: { description?: string }[];
};

type StrategyDoc = { campaign_proposals?: CampaignProposal[] };

type AudienceSummary = { memberCount?: number | null };

// An OBJECT, never a list. `recipients.length` read `undefined` on every real
// campaign, so the audience size never reached the owner.
type ProcessRecipients = {
	contact_ids?: string[];
	audience_ids?: string[];
	audience_summaries?: AudienceSummary[];
};

type ProcessDoc = {
	title?: string;
	user_summary?: string;
	description?: string;
	artifact_ids?: string[];
	// Artifacts the owner left out in the dock. They keep their membership in
	// `artifact_ids` so they can be restored, and activation excludes them.
	skipped_artifact_ids?: string[];
	recipients?: ProcessRecipients;
	// The drop writes `cancelled` here, and the link the owner was texted is
	// permanent. Without this field the reload had no way to know.
	status?: string;
};

const plural = (count: number, one: string, many: string) =>
	`${count} ${count === 1 ? one : many}`;

/**
 * Why the page could not load, kept apart because the two need opposite words.
 *
 * `missing` is the campaign genuinely not being in the plan. `unavailable` is a
 * 500, a dropped request or an expired token. One flag carried both, so every
 * one of those told the owner their campaign was gone. The link is permanent
 * and the reply they send fetches the same URL, so the same failure repeats.
 */
type LoadError = 'missing' | 'unavailable';

// Only a 404 on the PLAN proves absence. A 404 on the campaign's Process means
// the work behind a campaign that is still in the plan has gone, which is not
// the same thing and must not send the owner back for a "fresh link" to the very
// same URL. Anything else is unknown, and unknown is never reported as gone.
const loadErrorOf = (error: unknown, stage: 'plan' | 'process'): LoadError =>
	stage === 'plan' && error instanceof RestApiError && error.status === 404
		? 'missing'
		: 'unavailable';

/**
 * How many people this campaign will text, or null when that cannot be known.
 *
 * Audiences carry their own `memberCount`, and one missing count makes the total
 * unknowable. An understated number is worse than no number here, because the
 * owner reads it at the moment they agree to real customer sends.
 */
const audienceSize = (recipients?: ProcessRecipients): number | null => {
	if (!recipients) return null;
	const summaries = recipients.audience_summaries ?? [];
	if (summaries.some((a) => typeof a.memberCount !== 'number')) return null;
	return (
		(recipients.contact_ids?.length ?? 0) +
		summaries.reduce((sum, a) => sum + (a.memberCount ?? 0), 0)
	);
};

export const StartCampaign = () => {
	const { strategyId = '', campaignId = '' } = useParams();
	const api = useBridgewayApi();
	const userId = useUserId();

	const [campaign, setCampaign] = useState<CampaignProposal | null>(null);
	const [process, setProcess] = useState<ProcessDoc | null>(null);
	const [loadError, setLoadError] = useState<LoadError | null>(null);
	const [reloadKey, setReloadKey] = useState(0);
	const [busy, setBusy] = useState<'start' | 'reject' | null>(null);
	const [started, setStarted] = useState(false);
	const [rejected, setRejected] = useState(false);
	const [startError, setStartError] = useState<string | null>(null);
	// A go-live request that threw on the client may still have been accepted,
	// and it sends immediately. From here on nothing may claim nothing was sent,
	// and the drop must not cancel a Process that could be mid-send.
	const [startAttempted, setStartAttempted] = useState(false);

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			let stage: 'plan' | 'process' = 'plan';
			try {
				const strategy = await api.get<StrategyDoc>(
					`/api/user/${userId}/strategies/${strategyId}`,
				);
				const found = (strategy.campaign_proposals ?? []).find(
					(c) => c.id === campaignId,
				);
				if (cancelled) return;
				if (!found) {
					setLoadError('missing');
					return;
				}
				setCampaign(found);
				// Already running before this page loaded. Show it as started
				// rather than offering a button that would be refused.
				if (found.status === 'activated') setStarted(true);

				if (found.process_id) {
					stage = 'process';
					const doc = await api.get<ProcessDoc>(
						`/api/user/${userId}/processes/${found.process_id}`,
					);
					if (!cancelled) {
						setProcess(doc);
						// Same reason as `activated` above, for the other terminal
						// state. The drop cancels the Process and deliberately does
						// not edit the locked plan, so the proposal still reads
						// un-dropped. Without this the owner re-opened their
						// permanent link, saw no sign they had declined it, and was
						// offered the Start button again.
						if (doc.status === 'cancelled') setRejected(true);
						// The Process is what actually sends, and it turns
						// `active` BEFORE the proposal marker turns `activated`.
						// That marker write can also fail outright and leave the
						// campaign reading `materialized` while the work runs. So
						// the proposal alone cannot say whether anything has gone
						// out, and reading it alone offered "drop it" and
						// "nothing was sent" over a live send. `paused` counts
						// too: a Process only reaches it from `active`, so that
						// campaign has already started.
						else if (doc.status === 'active' || doc.status === 'paused')
							setStarted(true);
					}
				}
			} catch (error) {
				logger.error('start-campaign: could not load the campaign', error);
				if (!cancelled) setLoadError(loadErrorOf(error, stage));
			}
		};
		if (userId && strategyId && campaignId) void load();
		return () => {
			cancelled = true;
		};
	}, [api, userId, strategyId, campaignId, reloadKey]);

	const start = useCallback(async () => {
		setBusy('start');
		setStartError(null);
		try {
			await api.post(
				`/api/user/${userId}/strategies/${strategyId}/campaigns/${campaignId}/go-live`,
				// The owner read what goes out on this page and pressed the button.
				// That IS the explicit send decision, the same one the strategy dock
				// makes. Without it the route defaults to false and every
				// on-activation send is parked as `held`, waiting on a dispatch call
				// only the web panel makes. The page then said "Bridgeway will text
				// you as it goes" while nothing had gone.
				{ send_immediately: true },
			);
			setStarted(true);
		} catch (error) {
			logger.error('start-campaign: go-live failed', error);
			// It used to say "Nothing has been sent." The request sends
			// immediately and runs for minutes, so a failure the client sees can
			// sit on top of a send the server already began. Only a refusal
			// BEFORE the work starts proves nothing went out, and a 409 is the
			// one status that proves it.
			const refused = error instanceof RestApiError && error.status === 409;
			// Only an outcome we cannot vouch for withdraws the drop. A refusal
			// proves the work never began, so declining is still safe and the
			// owner keeps a way off this page.
			if (!refused) setStartAttempted(true);
			setStartError(
				refused
					? 'That did not go through. Nothing has been sent. Please try again.'
					: 'Bridgeway could not confirm that. Some messages may already be on their way, so check before you try again.',
			);
		} finally {
			setBusy(null);
		}
	}, [api, userId, strategyId, campaignId]);

	const reject = useCallback(async () => {
		setBusy('reject');
		setStartError(null);
		try {
			// Cancel the built work, never edit the plan. A campaign only exists
			// once the strategy is on, and `PATCH /strategies/{id}/plan` refuses
			// every edit while it is: "a running strategy's plan is locked —
			// switch it off before changing it". So `drop_campaign` returns 409 in
			// the one state this button is ever shown in.
			if (!campaign?.process_id) {
				setStartError('That campaign is still being built. Try again shortly.');
				return;
			}
			await api.patch(`/api/user/${userId}/processes/${campaign.process_id}`, {
				status: 'cancelled',
			});
			setRejected(true);
		} catch (error) {
			logger.error('start-campaign: reject failed', error);
			setStartError('That did not go through. The campaign is still here.');
		} finally {
			setBusy(null);
		}
	}, [api, userId, campaign]);

	if (loadError === 'missing') {
		return (
			<main className={styles.page}>
				<h1 className={styles.title}>This campaign is not here</h1>
				<p className={styles.note}>
					Reply to the text and Bridgeway will send you a fresh link.
				</p>
			</main>
		);
	}

	if (loadError === 'unavailable') {
		return (
			<main className={styles.page}>
				<h1 className={styles.title}>Bridgeway could not load this</h1>
				<p className={styles.note}>
					Your campaign is still here. Something went wrong on the way to it.
				</p>
				<button
					type="button"
					className={styles.start}
					onClick={() => {
						// The plan request can succeed and the Process request then
						// fail, so `campaign` may already be set. Without this the
						// retry rendered the campaign at once with no message count
						// and no recipient count, and a live Start button under them.
						setCampaign(null);
						setProcess(null);
						setLoadError(null);
						setReloadKey((k) => k + 1);
					}}
					data-testid="retry-load"
				>
					Try again
				</button>
			</main>
		);
	}

	if (!campaign) {
		return (
			<main className={styles.page}>
				<p className={styles.note}>Loading…</p>
			</main>
		);
	}

	// The skipped ones stay in `artifact_ids` so the owner can restore them, and
	// activation leaves them out. Counting the whole list told an owner who had
	// skipped two of five that five would go, at the moment they agreed to send.
	const skipped = process?.skipped_artifact_ids ?? [];
	const messageCount = (process?.artifact_ids ?? []).filter(
		(id) => !skipped.includes(id),
	).length;
	const recipientCount = audienceSize(process?.recipients);
	const facts = [
		messageCount ? plural(messageCount, 'message', 'messages') : '',
		recipientCount ? plural(recipientCount, 'person', 'people') : '',
	].filter(Boolean);

	return (
		<main className={styles.page}>
			<h1 className={styles.title}>{campaign.title || 'Your campaign'}</h1>
			{facts.length > 0 && <p className={styles.facts}>{facts.join(' · ')}</p>}

			{campaign.aim && <p className={styles.aim}>{campaign.aim}</p>}

			{process?.user_summary && (
				<p className={styles.summary}>{process.user_summary}</p>
			)}

			{(campaign.steps ?? []).length > 0 && (
				<ol className={styles.steps}>
					{(campaign.steps ?? [])
						.filter((step) => step?.description)
						.map((step, index) => (
							<li key={index}>{step.description}</li>
						))}
				</ol>
			)}

			{rejected ? (
				<div
					className={styles.done}
					role="status"
					data-testid="campaign-rejected"
				>
					<p className={styles.doneTitle}>Campaign dropped</p>
					{/* Says what the button did, which is cancel the built work.
					    It used to say the campaign had been taken out of the plan.
					    The plan is locked while the strategy runs, so the drop
					    never edits it, and the owner was told about a change that
					    did not happen. */}
					<p className={styles.note}>
						This campaign will not run, and nothing was sent.
					</p>
				</div>
			) : started ? (
				<div
					className={styles.done}
					role="status"
					data-testid="campaign-started"
				>
					<p className={styles.doneTitle}>Campaign started</p>
					{/* A paused Process reaches this branch too, because it can only
					    reach `paused` from `active`. The heading stays true — it did
					    start — but the note did not: "close this page" told the owner
					    no action was needed when only they can restart it, and "as it
					    goes" promised texts from a campaign sending nothing.
					    `ActivateStrategy.tsx` already tells the truth for this state. */}
					<p className={styles.note}>
						{process?.status === 'paused'
							? 'It is paused, so nothing is going out right now. Nothing more sends until it is started again.'
							: 'You can close this page. Bridgeway will text you as it goes.'}
					</p>
				</div>
			) : (
				<>
					<button
						type="button"
						className={styles.start}
						onClick={start}
						disabled={busy !== null}
						data-testid="start-campaign"
					>
						{busy === 'start' ? 'Starting…' : 'Start campaign'}
					</button>
					{!startAttempted && (
						<p className={styles.note}>Nothing sends until you tap this.</p>
					)}
					{/* Gone once Start has been tapped. The drop cancels the Process,
					    which is what sends, and a go-live that failed on the client
					    may already be sending. Cancelling then stops a live campaign
					    part way and records it as one that never ran. Retrying the
					    start stays available, because that is the safe direction. */}
					{!startAttempted && (
						<button
							type="button"
							className={styles.reject}
							onClick={reject}
							disabled={busy !== null}
							data-testid="reject-campaign"
						>
							{busy === 'reject' ? 'Dropping…' : 'No thanks, drop it'}
						</button>
					)}
					{startError && (
						<p className={styles.error} role="alert">
							{startError}
						</p>
					)}
				</>
			)}
		</main>
	);
};
